import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import { useLocation } from '@reach/router';
import { GatsbyBrowser, navigate, PageProps, WrapPageElementBrowserArgs } from 'gatsby';
import React, { PropsWithChildren, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useStore } from 'zustand';

import { backButtonStore } from '../commons/back';
import { QueryParameterWrapper } from '../commons/query-string';
import { useSiteSettings } from '../commons/site-settings';
import { currentUserStore } from '../commons/user';
import { isPreviewPath } from './preview';
import { trimStringRight } from './string';
import { currentTheme } from './theme';

type Props = PropsWithChildren<{
  pageContext: SilverbackPageContext;
}>;

const GeolocationRedirectWrapper = ({ children, pageContext }: Props) => {
  const websiteSettings = useSiteSettings(pageContext);
  const geoLocationAccess = useStore(currentUserStore, (state) => state.geoLocationAccess);
  const location = useLocation();
  const currentPath = trimStringRight(location.pathname, '/');
  const geoBlockingLandingPagePath = websiteSettings.geoBlockingLandingPage ? trimStringRight(websiteSettings.geoBlockingLandingPage.path, '/') : undefined;

  // If the user is not allowed to access the website due to their geolocation,
  // then we need to redirect to the geolocation blocking landing page.
  if (geoBlockingLandingPagePath && geoLocationAccess === false && currentPath !== geoBlockingLandingPagePath && !isPreviewPath(currentPath)) {
    navigate(geoBlockingLandingPagePath);
  }

  return <>{children}</>;
}

// Wrap everything inside a component, otherwise react hook rules
// complains about the use of useMessages during SSR.
const PageWrapper = ({ children, pageContext }: Props) => {

  useEffect(() => {
    backButtonStore.getState().update({
      last_visited_content_type: pageContext.typeName || ''
    });
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 2, // 2 hours
        staleTime: 1000 * 60 * 60 * 2, // 2 hours
      },
    },
  });

  // For Bergeinsatz, we want to redirect users to a landing page if they try to
  // accesss the website from a restricted country.
  if (currentTheme === 'bergeinsatz') {
    return (
      <QueryClientProvider client={queryClient}>
        <QueryParameterWrapper>
          <GeolocationRedirectWrapper pageContext={pageContext}>{children}</GeolocationRedirectWrapper>
        </QueryParameterWrapper>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <QueryParameterWrapper>{children}</QueryParameterWrapper>
    </QueryClientProvider>
  );
};

export const WrapPageElement: GatsbyBrowser['wrapPageElement'] = ({
  element,
  props,
}: WrapPageElementBrowserArgs<any, SilverbackPageContext> & {
  props: PageProps<any, SilverbackPageContext>;
}) => <PageWrapper pageContext={props.pageContext}>{element}</PageWrapper>;
